import { t } from '@lingui/core/macro'
import type { DocumentTab } from 'features/knowledgeDocumentViewer/types'
import { NEW_AGENTIC_CHAT_TAB_LABEL } from 'features/knowledgeDocumentViewer/utils/agenticChatTabFactories'
import { SelectedTab } from 'features/navbar/types'

type HistoryLabel = {
  label: string
  kind: 'new-tab' | 'new-search' | 'other'
}

function getWorkspaceTabHistoryLabel(tab: DocumentTab): HistoryLabel {
  const item = tab.history[tab.currentIndex]
  if (item.type === 'search') {
    const query = item.searchState.query.trim()
    if (query) return { label: query, kind: 'other' }
    if (
      tab.workspaceDestination === SelectedTab.Documents &&
      item.searchState.browseAll === true
    ) {
      return { label: t`Documents`, kind: 'other' }
    }
    if (item.searchState.searchMode === 'normal') {
      return { label: t`New Search`, kind: 'new-search' }
    }

    const hasDocChatConversation =
      (item.searchState.docChat?.conversation.turns.length ?? 0) > 0
    return hasDocChatConversation
      ? { label: t`AI Search`, kind: 'other' }
      : { label: t`New Tab`, kind: 'new-tab' }
  }

  if (item.type === 'destination') {
    return { label: item.label, kind: 'other' }
  }

  if (item.type === 'document') {
    const fromQuery = item.searchOriginQuery?.trim() || item.query.trim()
    if (fromQuery) return { label: fromQuery, kind: 'other' }
    return { label: t`Document`, kind: 'other' }
  }

  if (item.type === 'artifact') {
    return { label: t`Document`, kind: 'other' }
  }

  return { label: t`New Tab`, kind: 'new-tab' }
}

/**
 * Resolves the display title for a workspace tab.
 * Page-published titles (`customLabel`) win; otherwise fall back to history.
 */
export function getWorkspaceTabLabel(tab: DocumentTab): string {
  return tab.customLabel || getWorkspaceTabHistoryLabel(tab).label
}

/**
 * Title the documents page should publish onto the active workspace tab.
 * `undefined` keeps the current label; `null` clears it.
 */
export function resolvePublishedSearchTabTitle({
  currentDocumentId,
  tab,
}: {
  currentDocumentId?: string | null
  tab: DocumentTab | undefined
}): string | null | undefined {
  if (currentDocumentId || !tab) return undefined

  const historyLabel = getWorkspaceTabHistoryLabel(tab)
  const item = tab.history[tab.currentIndex]
  const chatOnly =
    item?.type === 'search' &&
    (item.searchState.chatOnly === true ||
      item.searchState.docChat !== undefined)

  if (chatOnly) {
    const autoTitle =
      historyLabel.kind === 'new-tab'
        ? NEW_AGENTIC_CHAT_TAB_LABEL
        : historyLabel.label
    if (
      tab.customLabel !== undefined &&
      tab.customLabel !== NEW_AGENTIC_CHAT_TAB_LABEL &&
      tab.customLabel !== autoTitle
    ) {
      return tab.customLabel
    }
    return autoTitle
  }

  if (historyLabel.kind === 'new-tab' || historyLabel.kind === 'new-search') {
    return null
  }
  return historyLabel.label
}
